import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, AlertTriangle } from "lucide-react";

interface AssinaturaCancelModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscriptionId: string;
  professionalName: string;
  onSuccess: () => void;
}

export function AssinaturaCancelModal({
  open,
  onOpenChange,
  subscriptionId,
  professionalName,
  onSuccess
}: AssinaturaCancelModalProps) {
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");

  const handleCancel = async () => {
    if (!reason.trim()) {
      toast.error("Por favor, informe o motivo do cancelamento");
      return;
    }

    try {
      setLoading(true);

      const { data, error } = await supabase.functions.invoke('pagarme-cancel-subscription', {
        body: {
          subscription_id: subscriptionId,
          cancellation_reason: reason
        }
      });

      if (error) throw error;

      toast.success("Assinatura cancelada com sucesso");
      onSuccess();
      onOpenChange(false);
      setReason("");
    } catch (error) {
      console.error("Error canceling subscription:", error);
      toast.error(error instanceof Error ? error.message : "Erro ao cancelar assinatura");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Cancelar Assinatura
          </DialogTitle>
          <DialogDescription>
            Esta ação cancelará a assinatura de <strong>{professionalName}</strong>.
            O profissional não terá mais acesso à plataforma após o término do período pago.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="reason">Motivo do Cancelamento *</Label>
            <Textarea
              id="reason"
              placeholder="Informe o motivo do cancelamento..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={loading}
              rows={4}
            />
            <p className="text-xs text-muted-foreground">
              Este motivo será registrado no histórico da assinatura.
            </p>
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="flex-1"
            >
              Voltar
            </Button>
            <Button
              onClick={handleCancel}
              disabled={loading || !reason.trim()}
              variant="destructive"
              className="flex-1"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Cancelando...
                </>
              ) : (
                "Confirmar Cancelamento"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
